import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Input, Space, Tag, Modal, Typography, message, Descriptions, Badge, Divider, Switch, Card, Row, Col, Select, Statistic } from "antd";
import { SearchOutlined, ReloadOutlined, EyeOutlined, DownloadOutlined, CloseOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined, TeamOutlined } from "@ant-design/icons";
import AdminLayout from "./Drawer";
import { formatDate, formatDateTime } from "../comman";
import "./userList.css";
import * as XLSX from 'xlsx';

const { Text } = Typography;
const { Option } = Select;

const VIPUsersList = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
    fetchAllEvents();
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [usersData, selectedEvent, searchText]);

  const fetchAllEvents = async () => {
    try {
      const response = await fetch("/api/get/events", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === "success") {
        setEvents(data.data);
      } else {
        console.error("Error in response data:", data);
      }
    } catch (error) {
      console.error("Fetch error:", error.message);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch("/api/new/get/events", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === "success") {
        setList(data.data);
      } else {
        console.error("Error in response data:", data);
      }
    } catch (error) {
      console.error("Fetch error:", error.message);
      message.error("Failed to fetch events");
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/vip/get/users", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === "success") {
        setUsersData(data.data);
      } else {
        console.error("Error in response data:", data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (record) => {
    try {
      const newStatus = record.status === "1" ? 0 : 1;
      const response = await fetch(`/api/vip/toggle-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: record.eventId,
          mobileNumber: record.mobileNumber,
          status: newStatus
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setUsersData((prev) =>
          prev.map((item) =>
            item.mobileNumber === record.mobileNumber
              ? { ...item, status: newStatus.toString() }
              : item
          )
        );
        message.success(`User ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      } else {
        message.error(data.message || "Something went wrong!");
      }
    } catch (error) {
      console.error(error);
      message.error("Error updating user status. Please try again.");
    }
  };

  const handleStatus = (value) => {
    const event = list && list?.filter((item) => item._id === value)[0];
    return event?.eventName;
  };

  const applyFilters = () => {
    let filtered = [...usersData];

    // Filter by event - check if user has attendance for that event
    if (selectedEvent !== "all") {
      filtered = filtered.filter((item) => {
        // Check if user has this event in their attendance array
        const hasEventInAttendance = Array.isArray(item.attendance) &&
          item.attendance.some((att) => att.eventId === selectedEvent);

        return hasEventInAttendance;
      });
    }

    // Filter by search text
    if (searchText) {
      const searchValue = searchText.toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          item?.hexaCode?.toLowerCase().includes(searchValue) ||
          item?.lotNumber?.toString().toLowerCase().includes(searchValue) ||
          item?.firstName?.toLowerCase().includes(searchValue) ||
          item?.mobileNumber?.toString().includes(searchValue)
        );
      });
    }

    setFilteredData(filtered);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleReset = () => {
    setSearchText("");
    setSelectedEvent("all");
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    let dataToAnalyze = usersData;
    let eventSpecificStats = null;

    // If specific event is selected
    if (selectedEvent !== "all") {
      // Users who have this event in their attendance array
      const usersWithEvent = usersData.filter(u => {
        return Array.isArray(u.attendance) &&
          u.attendance.some(att => att.eventId === selectedEvent);
      });

      // Users who actually attended this specific event (present: true)
      const attendedEvent = usersData.filter(u => {
        if (!Array.isArray(u.attendance)) return false;
        return u.attendance.some(att =>
          att.eventId === selectedEvent && att.present === true
        );
      });

      // Total attendance records for this event (present: true)
      const totalEventAttendance = usersData.reduce((sum, user) => {
        if (!Array.isArray(user.attendance)) return sum;
        const eventAttendances = user.attendance.filter(att =>
          att.eventId === selectedEvent && att.present === true
        );
        return sum + eventAttendances.length;
      }, 0);

      const activeInEvent = usersWithEvent.filter(u => u.status === "1").length;
      const inactiveInEvent = usersWithEvent.filter(u => u.status === "0" || !u.status).length;

      eventSpecificStats = {
        registeredForEvent: usersWithEvent.length,
        attendedEvent: attendedEvent.length,
        activeInEvent,
        inactiveInEvent,
        totalEventAttendance,
        eventAttendanceRate: usersWithEvent.length > 0
          ? ((attendedEvent.length / usersWithEvent.length) * 100).toFixed(1)
          : 0,
        eventActivationRate: usersWithEvent.length > 0
          ? ((activeInEvent / usersWithEvent.length) * 100).toFixed(1)
          : 0,
      };

      dataToAnalyze = usersWithEvent;
    }

    // Overall statistics
    const totalRegistrations = dataToAnalyze.length;
    const activeUsers = dataToAnalyze.filter(u => u.status === "1").length;
    const inactiveUsers = dataToAnalyze.filter(u => u.status === "0" || !u.status).length;

    // Count users with any attendance (present: true)
    const usersWithAttendance = dataToAnalyze.filter(u => {
      if (!Array.isArray(u.attendance)) return false;
      return u.attendance.some(att => att.present === true);
    }).length;

    // Total attendance across all events (only present: true)
    const totalAttendance = dataToAnalyze.reduce((sum, user) => {
      if (!Array.isArray(user.attendance)) return sum;
      return sum + user.attendance.filter(att => att.present === true).length;
    }, 0);

    return {
      totalRegistrations,
      activeUsers,
      inactiveUsers,
      totalAttendance,
      usersWithAttendance,
      activationRate: totalRegistrations > 0 ? ((activeUsers / totalRegistrations) * 100).toFixed(1) : 0,
      attendanceRate: totalRegistrations > 0 ? ((usersWithAttendance / totalRegistrations) * 100).toFixed(1) : 0,
      eventSpecificStats,
    };
  }, [usersData, selectedEvent]);

  const handleExport = () => {
    const exportData = rows.map(row => ({
      "Sr.No.": row.id,
      "Hexa Code": row.hexaCode,
      "Lot Number": row.lotNumber,
      "User Name": row.name,
      "Mobile Number": row.mobileNumber,
      "Invite Code": row.inviteCode,
      "Organization": row.organization,
      "Attendance Days": row.attendanceDays,
      "Status": row.status === "1" ? "Activated" : "Not Activated",
      "Event": row.event,
      "Created At": row.creation,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "VIP Users");
    XLSX.writeFile(workbook, `VIP_Users_${new Date().toISOString().split('T')[0]}.xlsx`);
    message.success("Data exported successfully!");
  };

  const columns = [
    {
      title: "Sr.No.",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Hexa Code",
      dataIndex: "hexaCode",
      key: "hexaCode",
      width: 150,
      render: (hexaCode, record) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedUser(record);
            setOpenDetailsModal(true);
          }}
          style={{ padding: 0 }}
        >
          {hexaCode}
        </Button>
      ),
    },
    {
      title: "Lot Number",
      dataIndex: "lotNumber",
      key: "lotNumber",
      width: 150,
    },
    {
      title: "User Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 150,
    },
    {
      title: "Events Registered",
      dataIndex: "eventsRegistered",
      key: "eventsRegistered",
      width: 180,
      align: "center",
      render: (eventsCount, record) => (
        <Tag color="purple">
          {eventsCount} {eventsCount === 1 ? 'Event' : 'Events'}
        </Tag>
      ),
    },
    {
      title: "Invite Code",
      dataIndex: "inviteCode",
      key: "inviteCode",
      width: 150,
    },
    {
      title: "Organization",
      dataIndex: "organization",
      key: "organization",
      width: 200,
    },
    {
      title: "Attendance Days",
      dataIndex: "attendanceDays",
      key: "attendanceDays",
      width: 120,
      align: "center",
      sorter: (a, b) => a.attendanceDays - b.attendanceDays,
      render: (days) => (
        <Tag color={days > 0 ? "blue" : "default"}>
          {days} {days === 1 ? 'Day' : 'Days'}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      filters: [
        { text: "Activated", value: "1" },
        { text: "Not Activated", value: "0" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <Switch
          checked={status === "1"}
          onChange={() => handleStatusToggle(record)}
          checkedChildren="Activated"
          unCheckedChildren="Not Activated"
        />
      ),
    },
  ];

  const rows = filteredData.map((item, index) => {
    // Calculate attendance days based on present: true
    const attendanceDays = Array.isArray(item?.attendance)
      ? item.attendance.filter(att => att.present === true).length
      : 0;

    // Count how many unique events user is registered for
    const eventsRegistered = Array.isArray(item?.attendance)
      ? item.attendance.length
      : 0;

    // Get list of event names for this user
    const userEventNames = Array.isArray(item?.attendance) && list.length > 0
      ? item.attendance.map(att => {
        const event = list.find(e => e._id === att.eventId);
        return event ? event.eventName : 'Unknown Event';
      })
      : [];

    return {
      key: item._id || index,
      id: index + 1,
      hexaCode: item?.hexaCode,
      lotNumber: item?.lotNumber,
      name: item?.firstName,
      mobileNumber: item?.mobileNumber,
      inviteCode: item?.inviteCode,
      organization: item?.organization,
      pincode: item?.pincode,
      address: item?.address,
      event: handleStatus(item?.eventId),
      creation: item?.createdAt && formatDateTime(item.createdAt),
      eventId: item?.eventId,
      gate: item?.gate,
      status: item?.status || "0",
      attendanceDays,
      eventsRegistered,
      userEventNames,
      attendance: item?.attendance || [],
      idFile: item?.idFile,
      photographerName: item?.photographerName,
      cameramanName: item?.cameramanName,
      followersCount: item?.followersCount,
      socialMediaLinks: item?.socialMediaLinks,
      pressType: item?.pressType
    };
  });

  return (
    <AdminLayout>
      <p style={{ fontSize: "1em", fontWeight: "bold", marginBottom: "5px" }}>
        Manage VIP Users
      </p>
      <div style={{ padding: "24px", background: "#f0f2f5" }}>
        {/* Dashboard Statistics */}
        <div style={{ marginBottom: 24 }}>
          <Card>
            <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <Text strong style={{ fontSize: 16 }}>Filter by Event:</Text>
              <Select
                value={selectedEvent}
                onChange={setSelectedEvent}
                style={{ minWidth: 200, maxWidth: 300, flex: 1 }}
                placeholder="Select an event"
              >
                <Option value="all">All Events</Option>
                {events.map((event) => (
                  <Option key={event._id} value={event._id}>
                    {event.eventName}
                  </Option>
                ))}
              </Select>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <Card bordered={false} style={{ background: '#e6f7ff' }}>
                  <Statistic
                    title={selectedEvent === "all" ? "Total Users" : "Event Registrations"}
                    value={statistics.eventSpecificStats ? statistics.eventSpecificStats.registeredForEvent : statistics.totalRegistrations}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <Card bordered={false} style={{ background: '#f6ffed' }}>
                  <Statistic
                    title="Active Users"
                    value={statistics.eventSpecificStats ? statistics.eventSpecificStats.activeInEvent : statistics.activeUsers}
                    prefix={<CheckCircleOutlined />}
                    suffix={`(${statistics.eventSpecificStats ? statistics.eventSpecificStats.eventActivationRate : statistics.activationRate}%)`}
                    valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <Card bordered={false} style={{ background: '#fff1f0' }}>
                  <Statistic
                    title="Inactive Users"
                    value={statistics.eventSpecificStats ? statistics.eventSpecificStats.inactiveInEvent : statistics.inactiveUsers}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f', fontSize: '20px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <Card bordered={false} style={{ background: '#f9f0ff' }}>
                  <Statistic
                    title={selectedEvent === "all" ? "Total Attended" : "Users Attended"}
                    value={statistics.eventSpecificStats ? statistics.eventSpecificStats.attendedEvent : statistics.usersWithAttendance}
                    prefix={<TeamOutlined />}
                    suffix={`(${statistics.eventSpecificStats ? statistics.eventSpecificStats.eventAttendanceRate : statistics.attendanceRate}%)`}
                    valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Main Table Section */}
        <div style={{ background: "#fff", padding: "24px", borderRadius: "8px" }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <Input
                  placeholder="Search by hexa code, lot number, name, or mobile"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ minWidth: 250, maxWidth: 400, flex: 1 }}
                  allowClear
                />
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Reset
                </Button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                <Text type="secondary">
                  Showing {rows.length} of {usersData.length} records
                </Text>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                >
                  Export
                </Button>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={rows}
              loading={loading}
              pagination={{
                defaultPageSize: 100,
                pageSize: 100,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ["50", "100", "200", "500"],
                responsive: true,
              }}
              bordered
              size="middle"
              scroll={{ x: 1200 }}
            />
          </Space>
        </div>
      </div>

      {/* User Details Modal */}
      <Modal
        open={openDetailsModal}
        onCancel={() => setOpenDetailsModal(false)}
        footer={[
          <Button key="close" onClick={() => setOpenDetailsModal(false)}>
            Close
          </Button>,
          selectedUser?.idFile && (
            <Button
              key="view-doc"
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => {
                const normalizedPath = selectedUser.idFile.replace(/\\/g, '/');
                window.open(`${process.env.REACT_APP_DOCUMENT_PATH}/${normalizedPath}`, '_blank');
              }}
            >
              View ID
            </Button>
          ),
        ]}
        width="90%"
        style={{ maxWidth: 700, top: 20 }}
        closeIcon={<CloseOutlined />}
        title={
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 40,
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <span style={{ fontSize: '20px', fontWeight: 600 }}>User Details</span>
            <Badge
              status={selectedUser?.status === "1" ? "success" : "error"}
              text={selectedUser?.status === "1" ? "Activated" : "Not Activated"}
            />
          </div>
        }
      >
        <Divider />
        {selectedUser && (
          <div>
            <Divider orientation="left" style={{ marginTop: 2 }}>Basic Information</Divider>

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Hexa Code">
                {selectedUser.hexaCode}
              </Descriptions.Item>
              <Descriptions.Item label="Lot Number">
                {selectedUser.lotNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Full Name">
                {selectedUser.name}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile Number">
                {selectedUser.mobileNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Invite Code">
                {selectedUser.inviteCode}
              </Descriptions.Item>
              <Descriptions.Item label="Organization">
                {selectedUser.organization}
              </Descriptions.Item>
              <Descriptions.Item label="Attendance Days">
                <Tag color={selectedUser.attendanceDays > 0 ? "blue" : "default"}>
                  {selectedUser.attendanceDays} {selectedUser.attendanceDays === 1 ? 'Day' : 'Days'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Events Registered">
                <Tag color="purple">
                  {selectedUser.eventsRegistered} {selectedUser.eventsRegistered === 1 ? 'Event' : 'Events'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Event Names" span={1}>
                <Space wrap>
                  {selectedUser.userEventNames && selectedUser.userEventNames.length > 0 ? (
                    selectedUser.userEventNames.map((eventName, idx) => (
                      <Tag key={idx} color="cyan">{eventName}</Tag>
                    ))
                  ) : (
                    <Text type="secondary">No events</Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedUser.status === "1" ? "success" : "error"}>
                  {selectedUser.status === "1" ? "Activated" : "Not Activated"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {selectedUser.address || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Event">
                {selectedUser.event || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {selectedUser.creation}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default VIPUsersList;

// import React, { useEffect, useState } from "react";
// import { Table, Button, Input, Space, Tag, Modal, Typography, message, Descriptions, Badge, Divider, Switch } from "antd";
// import { SearchOutlined, ReloadOutlined, EyeOutlined, DownloadOutlined, CloseOutlined } from "@ant-design/icons";
// import AdminLayout from "./Drawer";
// import { formatDate, formatDateTime } from "../comman";
// import "./userList.css";
// import * as XLSX from 'xlsx';

// const { Text } = Typography;

// const VIPUsersList = () => {
//   const [list, setList] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [searchText, setSearchText] = useState("");
//   const [filteredData, setFilteredData] = useState([]);
//   const [usersData, setUsersData] = useState([]);
//   const [openDetailsModal, setOpenDetailsModal] = useState(false);
//   const [selectedUser, setSelectedUser] = useState(null);

//   useEffect(() => {
//     fetchEvents();
//   }, []);

//   useEffect(() => {
//     fetchAllUsers();
//   }, []);

//   useEffect(() => {
//     setFilteredData(usersData);
//   }, [usersData]);

//   const fetchEvents = async () => {
//     try {
//       const response = await fetch("/api/new/get/events", {
//         method: "GET",
//         headers: {
//           "Content-Type": "application/json",
//           Accept: "application/json",
//         },
//       });

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       const data = await response.json();
//       if (data.status === "success") {
//         setList(data.data);
//       } else {
//         console.error("Error in response data:", data);
//       }
//     } catch (error) {
//       console.error("Fetch error:", error.message);
//       message.error("Failed to fetch events");
//     }
//   };

//   const fetchAllUsers = async () => {
//     setLoading(true);
//     try {
//       const response = await fetch("/api/vip/get/users", {
//         method: "GET",
//         headers: {
//           "Content-Type": "application/json",
//           Accept: "application/json",
//         },
//       });

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       const data = await response.json();
//       if (data.status === "success") {
//         setUsersData(data.data);
//       } else {
//         console.error("Error in response data:", data);
//       }
//     } catch (error) {
//       console.error("Error fetching users:", error);
//       message.error("Failed to fetch users");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleStatusToggle = async (record) => {
//     try {
//       const newStatus = record.status === "1" ? 0 : 1;
//       const response = await fetch(`/api/vip/toggle-status`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           userId: record.eventId,
//           mobileNumber: record.mobileNumber,
//           status: newStatus
//         }),
//       });

//       const data = await response.json();
//       if (response.ok) {
//         setUsersData((prev) =>
//           prev.map((item) =>
//             item.mobileNumber === record.mobileNumber
//               ? { ...item, status: newStatus }
//               : item
//           )
//         );
//         message.success(`User ${newStatus === "1" ? 'activated' : 'deactivated'} successfully!`);
//       } else {
//         message.error(data.message || "Something went wrong!");
//       }
//     } catch (error) {
//       console.error(error);
//       message.error("Error updating user status. Please try again.");
//     }
//   };

//   const handleStatus = (value) => {
//     const event = list && list?.filter((item) => item._id === value)[0];
//     return event?.eventName;
//   };

//   const handleSearch = (value) => {
//     setSearchText(value);
//     if (!value) {
//       setFilteredData(usersData);
//       return;
//     }

//     const searchValue = value.toLowerCase();
//     const filtered = usersData.filter((item) => {
//       return (
//         item?.hexaCode?.toLowerCase().includes(searchValue) ||
//         item?.lotNumber?.toString().toLowerCase().includes(searchValue) ||
//         item?.firstName?.toLowerCase().includes(searchValue) ||
//         item?.mobileNumber?.toString().includes(searchValue)
//       );
//     });
//     setFilteredData(filtered);
//   };

//   const handleReset = () => {
//     setSearchText("");
//     setFilteredData(usersData);
//   };

//   const handleExport = () => {
//     const exportData = rows.map(row => ({
//       "Sr.No.": row.id,
//       "Hexa Code": row.hexaCode,
//       "Lot Number": row.lotNumber,
//       "User Name": row.name,
//       "Mobile Number": row.mobileNumber,
//       "Designation": row.designation,
//       "Organization": row.organization,
//       "Attendance Days": row.attendanceDays,
//       "Status": row.status === "1" ? "Activated" : "Not Activated",
//       "Created At": row.creation,
//     }));

//     const worksheet = XLSX.utils.json_to_sheet(exportData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "VIP Users");
//     XLSX.writeFile(workbook, `VIP_Users_${new Date().toISOString().split('T')[0]}.xlsx`);
//     message.success("Data exported successfully!");
//   };

//   const columns = [
//     {
//       title: "Sr.No.",
//       dataIndex: "id",
//       key: "id",
//       width: 80,
//       sorter: (a, b) => a.id - b.id,
//     },
//     {
//       title: "Hexa Code",
//       dataIndex: "hexaCode",
//       key: "hexaCode",
//       width: 150,
//       render: (hexaCode, record) => (
//         <Button
//           type="link"
//           onClick={() => {
//             setSelectedUser(record);
//             setOpenDetailsModal(true);
//           }}
//           style={{ padding: 0 }}
//         >
//           {hexaCode}
//         </Button>
//       ),
//     },
//     {
//       title: "Lot Number",
//       dataIndex: "lotNumber",
//       key: "lotNumber",
//       width: 150,
//     },
//     {
//       title: "User Name",
//       dataIndex: "name",
//       key: "name",
//       width: 150,
//       sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
//     },
//     {
//       title: "Mobile Number",
//       dataIndex: "mobileNumber",
//       key: "mobileNumber",
//       width: 150,
//     },
//     {
//       title: "Designation",
//       dataIndex: "designation",
//       key: "designation",
//       width: 150,
//     },
//     {
//       title: "Organization",
//       dataIndex: "organization",
//       key: "organization",
//       width: 200,
//     },
//     {
//       title: "Attendance Days",
//       dataIndex: "attendanceDays",
//       key: "attendanceDays",
//       width: 120,
//       align: "center",
//       sorter: (a, b) => a.attendanceDays - b.attendanceDays,
//       render: (days) => (
//         <Tag color={days > 0 ? "blue" : "default"}>
//           {days} {days === 1 ? 'Day' : 'Days'}
//         </Tag>
//       ),
//     },
//     {
//       title: "Status",
//       dataIndex: "status",
//       key: "status",
//       width: 120,
//       align: "center",
//       filters: [
//         { text: "Activated", value: 1 },
//         { text: "Not Activated", value: 0 },
//       ],
//       onFilter: (value, record) => record.status === value,
//       render: (status, record) => (
//         <Switch
//           checked={status === "1"}
//           onChange={() => handleStatusToggle(record)}
//           checkedChildren="Activated"
//           unCheckedChildren="Not Activated"
//         />
//       ),
//     },
//   ];

//   const rows = filteredData.map((item, index) => ({
//     key: item._id || index,
//     id: index + 1,
//     hexaCode: item?.hexaCode,
//     lotNumber: item?.lotNumber,
//     name: item?.firstName,
//     mobileNumber: item?.mobileNumber,
//     designation: item?.designation,
//     organization: item?.organization,
//     pincode: item?.pincode,
//     address: item?.address,
//     event: handleStatus(item?.eventId),
//     creation: item?.createdAt && formatDateTime(item.createdAt),
//     eventId: item?.eventId,
//     gate: item?.gate,
//     status: item?.status || "0",
//     attendanceDays: Array.isArray(item?.attendance) ? item.attendance.length : 0,
//     attendance: item?.attendance || [],
//     idFile: item?.idFile,
//     photographerName: item?.photographerName,
//     cameramanName: item?.cameramanName,
//     followersCount: item?.followersCount,
//     socialMediaLinks: item?.socialMediaLinks,
//     pressType: item?.pressType
//   }));

//   return (
//     <AdminLayout>
//       <div style={{ padding: "24px", background: "#f0f2f5" }}>
//         <div style={{ background: "#fff", padding: "24px", borderRadius: "8px" }}>
//           <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between", flexWrap: "wrap" }}>
//             <Space wrap>
//               <Input
//                 placeholder="Search by hexa code, lot number, name, or mobile"
//                 prefix={<SearchOutlined />}
//                 value={searchText}
//                 onChange={(e) => handleSearch(e.target.value)}
//                 style={{ width: 400 }}
//                 allowClear
//               />
//               <Button icon={<ReloadOutlined />} onClick={handleReset}>
//                 Reset
//               </Button>
//             </Space>
//             <Space>
//               <Text type="secondary">
//                 Showing {rows.length} of {usersData.length} records
//               </Text>
//               <Button
//                 type="primary"
//                 icon={<DownloadOutlined />}
//                 onClick={handleExport}
//               >
//                 Export
//               </Button>
//             </Space>
//           </Space>

//           <Table
//             columns={columns}
//             dataSource={rows}
//             loading={loading}
//             pagination={{
//               defaultPageSize: 100,
//               pageSize: 100,
//               showSizeChanger: true,
//               showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
//               pageSizeOptions: ["50", "100", "200", "500"],
//             }}
//             bordered
//             size="middle"
//             scroll={{ x: 1200 }}
//           />
//         </div>
//       </div>

//       {/* User Details Modal */}
//       <Modal
//         open={openDetailsModal}
//         onCancel={() => setOpenDetailsModal(false)}
//         footer={[
//           <Button key="close" onClick={() => setOpenDetailsModal(false)}>
//             Close
//           </Button>,
//           selectedUser?.idFile && (
//             <Button
//               key="view-doc"
//               type="primary"
//               icon={<EyeOutlined />}
//               onClick={() => {
//                 const normalizedPath = selectedUser.idFile.replace(/\\/g, '/');
//                 window.open(`${process.env.REACT_APP_DOCUMENT_PATH}/${normalizedPath}`, '_blank');
//               }}
//             >
//               View ID Document
//             </Button>
//           ),
//         ]}
//         width="90%"
//         style={{ maxWidth: 700 }}
//         closeIcon={<CloseOutlined />}
//         title={
//           <div style={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center',
//             paddingRight: 40
//           }}>
//             <span style={{ fontSize: '22px', fontWeight: 600 }}>User Details</span>
//             <Badge
//               status={selectedUser?.status === "1" ? "success" : "error"}
//               text={selectedUser?.status === "1" ? "Activated" : "Not Activated"}
//             />
//           </div>
//         }
//       >
//         <Divider />
//         {selectedUser && (
//           <div>
//             <Divider orientation="left" style={{ marginTop: 2 }}>Basic Information</Divider>

//             <Descriptions column={1} bordered size="small">
//               <Descriptions.Item label="Hexa Code">
//                 {selectedUser.hexaCode}
//               </Descriptions.Item>
//               <Descriptions.Item label="Lot Number">
//                 {selectedUser.lotNumber}
//               </Descriptions.Item>
//               <Descriptions.Item label="Full Name">
//                 {selectedUser.name}
//               </Descriptions.Item>
//               <Descriptions.Item label="Mobile Number">
//                 {selectedUser.mobileNumber}
//               </Descriptions.Item>
//               <Descriptions.Item label="Designation">
//                 {selectedUser.designation}
//               </Descriptions.Item>
//               <Descriptions.Item label="Organization">
//                 {selectedUser.organization}
//               </Descriptions.Item>
//               <Descriptions.Item label="Attendance Days">
//                 <Tag color={selectedUser.attendanceDays > 0 ? "blue" : "default"}>
//                   {selectedUser.attendanceDays} {selectedUser.attendanceDays === 1 ? 'Day' : 'Days'}
//                 </Tag>
//               </Descriptions.Item>
//               <Descriptions.Item label="Status">
//                 <Tag color={selectedUser.status === "1" ? "success" : "error"}>
//                   {selectedUser.status === "1" ? "Activated" : "Not Activated"}
//                 </Tag>
//               </Descriptions.Item>
//               <Descriptions.Item label="Address">
//                 {selectedUser.address || 'N/A'}
//               </Descriptions.Item>
//               <Descriptions.Item label="Event">
//                 {selectedUser.event || 'N/A'}
//               </Descriptions.Item>
//               <Descriptions.Item label="Created At">
//                 {selectedUser.creation}
//               </Descriptions.Item>
//             </Descriptions>
//           </div>
//         )}
//       </Modal>
//     </AdminLayout>
//   );
// };

// export default VIPUsersList;
